import React from "react";
import { ComputeConfigurationSection } from "./compute/ComputeConfigurationSection";
import { InstanceNameConfiguration } from "./instance-name/InstanceNameConfiguration";
import { NgcApiKeyConfiguration } from "./ngc-api-key/NgcApiKeyConfiguration";
import { ParameterConfiguration } from "./parameters/ParameterConfiguration";
import { useLaunchableDeployment } from "../hooks/useLaunchableDeployment";

interface LaunchableConfigurationProps {
  about?: React.ReactNode;
  onShowEditComputeModal: () => void;
}

export const LaunchableConfiguration: React.FC<
  LaunchableConfigurationProps
> = ({ about, onShowEditComputeModal }) => {
  const deployment = useLaunchableDeployment();

  return (
    <div
      className={
        about
          ? "grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]"
          : "grid min-w-0 items-start gap-6"
      }
    >
      <div className="min-w-0 overflow-hidden rounded-lg border border-zinc-700 bg-secondaryBg [&>section:first-child]:border-t-0">
        <InstanceNameConfiguration
          instanceName={deployment.instanceName}
          nameErrorMessage={deployment.nameErrorMessage}
          onChange={deployment.onInstanceNameChange}
        />
        <ComputeConfigurationSection
          instanceType={deployment.instanceType}
          defaultInstanceType={deployment.defaultInstanceType}
          intendedInstanceType={deployment.intendedInstanceType}
          intendedDiskStorage={deployment.intendedDiskStorage}
          instanceStorage={deployment.instanceStorage}
          instanceLoading={deployment.instanceLoading}
          intendedCloudCredentialId={deployment.intendedCloudCredentialId}
          intendedFirewallRules={deployment.intendedFirewallRules}
          imageId={deployment.imageId}
          allInstances={deployment.allInstances}
          selectedLocation={deployment.selectedLocation}
          providerDiffers={deployment.providerDiffers}
          isOutOfStock={
            deployment.selectedInstanceIsOutOfStock ||
            deployment.isInstanceOutOfStock
          }
          open={deployment.openSection === "compute"}
          onToggle={() => deployment.toggleSection("compute")}
          onShowEditComputeModal={onShowEditComputeModal}
          onInstanceChange={deployment.onInstanceChange}
          onStorageValidityChange={deployment.setStorageValid}
          onOutOfStockChange={deployment.setIsInstanceOutOfStock}
          onProviderDifferenceChange={deployment.setProviderDiffers}
          onLocationChange={deployment.setSelectedLocation}
        />
        <ParameterConfiguration
          parameters={deployment.parameters}
          values={deployment.parameterValues}
          secretSelections={deployment.parameterSecretSelections}
          availableSecrets={deployment.availableSecrets}
          secretsLoading={deployment.secretsLoading}
          organizationId={deployment.organizationId}
          collectValues={deployment.shouldCollectParameters}
          hasEditedValues={deployment.hasEditedParameters}
          missingRequiredCount={deployment.missingRequiredParameterCount}
          open={deployment.openSection === "parameters"}
          onToggle={() => deployment.toggleSection("parameters")}
          onChange={deployment.updateParameterValues}
          onSecretSelectionsChange={
            deployment.managedSecretsEnabled
              ? deployment.updateParameterSecretSelections
              : undefined
          }
          disabled={deployment.initLoading || deployment.loading}
        />
        <NgcApiKeyConfiguration
          visible={deployment.ngcApiKey.shouldShowSection}
          open={deployment.openSection === "ngc-api-key"}
          onToggle={() => deployment.toggleSection("ngc-api-key")}
          checked={deployment.ngcApiKey.checked}
          onCheckedChange={deployment.ngcApiKey.onCheckedChange}
          errorMessage={deployment.ngcApiKey.errorMessage}
          disabled={deployment.ngcApiKey.isDisabled}
          isAiPlaygroundsKey={deployment.ngcApiKey.isAiPlaygroundsKey}
        />
      </div>
      {about && <aside className="min-w-0 px-1 pt-1">{about}</aside>}
    </div>
  );
};
